import type { VacationDpRow } from '../models/vacationDpBoard.types';
import { isInactiveCollaborator } from '../../../../shared/vacations/collaboratorStatus';

/**
 * Enriquece as linhas do painel do DP com dados de alocação, tipo de contrato e foto.
 *
 * Lê `allocationSummary.active[]`, `collaboratorType` e `photoUrl` do documento do colaborador privado,
 * adicionando à linha:
 * - `clientNames`: nomes únicos de clientes (dedupe case-insensitive)
 * - `responsibleNames`: nomes únicos de responsáveis Raro (dedupe)
 * - `collaboratorType`: tipo de contrato (base do filtro de elegibilidade)
 * - `collaboratorStatus` e `collaboratorEndDate`: situação e data de desligamento (para excluir inativos)
 * - `collaboratorPhotoUrl`: URL da foto (para apresentação apenas)
 * - `clientResponsibles`: pares cliente-responsável dedupados para agrupamento
 *
 * A função é pura: se o colaborador não for encontrado ou não tiver alocações, os arrays
 * vêm vazios, `collaboratorType` e `collaboratorPhotoUrl` ficam `null`.
 */
export function enrichDpRows(
    rows: VacationDpRow[],
    collaborators: Map<string, any>
): VacationDpRow[] {
    return rows.map((row) => {
        const collaborator = collaborators.get(row.collaboratorId);

        if (!collaborator) {
            return {
                ...row,
                collaboratorType: null,
                collaboratorStatus: null,
                collaboratorEndDate: null,
                clientNames: [],
                responsibleNames: [],
                collaboratorPhotoUrl: null,
                clientResponsibles: [],
            };
        }

        // Extrair clientes e responsáveis das alocações ativas
        const allocations = collaborator.allocationSummary?.active ?? [];
        const clientNamesSet = new Set<string>();
        const responsibleNamesSet = new Set<string>();
        const clientResponsiblesMap = new Map<string, { clientName: string; responsibleId: string | null; responsibleName: string | null }>();

        allocations.forEach((allocation: any) => {
            if (allocation.clientName) {
                clientNamesSet.add(allocation.clientName);
            }
            if (allocation.responsibleName) {
                responsibleNamesSet.add(allocation.responsibleName);
            }
            if (allocation.clientName) {
                const key = `${allocation.clientName}|${allocation.responsibleId || 'null'}`;
                clientResponsiblesMap.set(key, {
                    clientName: allocation.clientName,
                    responsibleId: allocation.responsibleId ?? null,
                    responsibleName: allocation.responsibleName ?? null,
                });
            }
        });

        return {
            ...row,
            collaboratorType: collaborator.collaboratorType ?? null,
            collaboratorStatus: collaborator.status ?? null,
            collaboratorEndDate: collaborator.endDate ?? null,
            clientNames: Array.from(clientNamesSet),
            responsibleNames: Array.from(responsibleNamesSet),
            collaboratorPhotoUrl: collaborator.photoUrl ?? null,
            clientResponsibles: Array.from(clientResponsiblesMap.values()),
        };
    });
}

/**
 * Mantém só colaboradores elegíveis ao módulo de férias: Celetista e ativos.
 * Usa o mesmo critério do cálculo de saldo no backend.
 */
export function excludeNonVacationContracts(rows: VacationDpRow[], todayIso: string): VacationDpRow[] {
    const VACATION_ELIGIBLE_TYPES = ['Celetista'];
    return rows.filter(
        (row) =>
            VACATION_ELIGIBLE_TYPES.includes(row.collaboratorType ?? '') &&
            !isInactiveCollaborator(row.collaboratorStatus, row.collaboratorEndDate, todayIso)
    );
}

/**
 * Extrai um valor único de cliente a partir das linhas, para uso no filtro.
 * Usa normalização case-insensitive e ordena alfabeticamente.
 */
export function extractClientOptionsFromRows(rows: VacationDpRow[]): Array<{ value: string; label: string; count: number }> {
    const clientCounts = new Map<string, number>();
    const clientNormalized = new Map<string, string>(); // mapa de normalizado → original (para caso e acentuação)

    rows.forEach((row) => {
        row.clientNames?.forEach((name) => {
            const normalized = normalizeClientName(name);
            const current = clientCounts.get(normalized) ?? 0;
            clientCounts.set(normalized, current + 1);
            clientNormalized.set(normalized, name); // preserva a última variação vista
        });
    });

    return Array.from(clientCounts.entries())
        .map(([normalized, count]) => ({
            value: normalized,
            label: clientNormalized.get(normalized) || normalized,
            count,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

/**
 * Normaliza nome de cliente para comparação case-insensitive e deduplicação.
 * Remove espaços extras e converte para minúsculas.
 */
function normalizeClientName(name: string): string {
    return name.trim().toLowerCase();
}

/**
 * Filtra linhas por cliente (normalizado). `null` ou `''` retorna todas as linhas.
 */
export function filterRowsByClient(rows: VacationDpRow[], clientNormalized: string | null): VacationDpRow[] {
    if (!clientNormalized) return rows;

    return rows.filter((row) =>
        row.clientNames?.some((name) => normalizeClientName(name) === clientNormalized)
    );
}
